import { computed, defineAsyncComponent } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import useAccessibilityButton from "@/components/HomeLayouts/useAccessibilityButton.js";
import useSideMenu from "@/util/useSideMenu.js";
import useBackButton from "@/util/useBackButton.js";
import useQrButton from "@/util/useQrButton.js";
import useSearchV2 from "@/util/useSearchV2.ts";

export default function useCategoriesController() {
  const store = useStore();
  const route = useRoute();

  const {
    searchOptions,
    searchQuery,
    popularSearchOptions,
    onSearchQueryChanged,
    onSearchSelected,
    isSearchLoading,
    showSearchButton,
    getVisitedItems,
    clearVisitedItems,
  } = useSearchV2();

  const {
    sideMenuItems,
    sideMenuSelected,
    onSideMenuSelected,
    sideMenuOpenOnInit,
    setSideMenuOpenOnInit,
    showSideMenu,
  } = useSideMenu();

  const { showQrButton } = useQrButton();

  const { loadAccessibilityPlugin } = useAccessibilityButton();

  const { goBack } = useBackButton();

  const sponsorMedia = computed(() => {
    const sponsorStyle = store.getters.getStoreVar(
      "Home",
      "styling",
      "dropdown",
      "sponsorStyle"
    );

    if (sponsorStyle === "none") {
      return [];
    }

    return store.getters.getStoreVar(
      "Home",
      "styling",
      "photo",
      "sponsorImages"
    );
  });

  const layoutComponents = new Map();

  const layoutController = computed(() => {
    const storeVars =
      route.name === "SubCategories"
        ? ["SubCategories", "subcategoryLayout"]
        : ["Categories", "categoriesLayout"];

    const layoutName = store.getters.getStoreVar(
      storeVars[0],
      "page",
      "layout",
      storeVars[1]
    );

    if (!layoutComponents.has(layoutName)) {
      layoutComponents.set(
        layoutName,
        defineAsyncComponent(
          () => import(`../../components/CategoriesLayouts/${layoutName}.vue`)
        )
      );
    }

    return layoutComponents.get(layoutName);
  });

  const isTouchScreen = computed(() => store.getters.getTouchScreen);

  function onAccessibilityButtonClick(): void {
    loadAccessibilityPlugin();
  }

  function onBackButtonClick(): void {
    goBack();
  }

  function onPageUnmounted({
    siteNavigatorIsOpen,
  }: {
    siteNavigatorIsOpen: boolean;
  }) {
    setSideMenuOpenOnInit(siteNavigatorIsOpen);
  }

  function onPageIdle(): void {
    clearVisitedItems();
  }

  return {
    isTouchScreen,
    layoutController,
    onAccessibilityButtonClick,
    onBackButtonClick,
    onSearchSelected,
    onSideMenuSelected,
    onSearchQueryChanged,
    searchOptions,
    searchQuery,
    popularSearchOptions,
    isSearchLoading,
    showQrButton,
    sideMenuItems,
    sideMenuSelected,
    sideMenuOpenOnInit,
    showSideMenu,
    sponsorMedia,
    onPageUnmounted,
    showSearchButton,
    getVisitedItems,
    onPageIdle,
  };
}
